import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import Link from 'brastrap/containers/link/LinkContainer';
import { pathFacetColours } from '../../../../shared/utils/product-listing/facet';
import { updateHrefLang } from '../../../../shared/actions/hreflang';

const resetPageParam = search => {
  if (!search) return '';
  const params = new URLSearchParams(search);
  params.delete('page');
  return params.toString() ? `?${params.toString()}` : '';
};

const FacetInput = props => {
  const {
    className,
    id,
    name,
    elementType,
    count,
    onChange,
    checked,
    selected,
    swatch,
    value,
    facetType,
    pathname,
    search,
    basePath,
  } = props;

  const inputProps = {
    id,
    onChange,
    value,
    checked: !!(checked || selected),
  };

  const linkClass = selected
    ? `${className} ${selected && 'c-results-facet--selected'}`
    : className;

  inputProps['aria-checked'] = inputProps.checked;

  const containerProps = {};
  if (className) {
    containerProps.className = className;
  }

  const input = (
    <input className="c-results-facet__check" type="checkbox" {...inputProps} />
  );
  const label = (
    <label className="c-results-facet__label" htmlFor={id}>
      {swatch && <span className="c-swatch" style={{ background: swatch }} />}
      {`${name}${!isNaN(count) ? ` (${count})` : ''}`}
    </label>
  );

  const isAllLingerie = pathname?.includes('/all-lingerie/');
  const isShopBy = pathname?.includes('/shop-by/');

  if (elementType === 'div' && !(isAllLingerie && facetType === 'colour')) {
    return (
      <div {...containerProps}>
        {input}
        {label}
      </div>
    );
  } else if (isAllLingerie && !isShopBy && facetType === 'colour') {
    /*
     To keep client-side routing intact while layering colour filters, we rewrite the pathname each time a colour is selected. When no colour is selected,
     each colour links to its own path such as /collections/all-lingerie/colours/blue
     Once a user chooses a colour — e.g. “black” — we append every additional, unselected colour to that choice, making paths like /collections/all-lingerie/colours/black+blue and /collections/all-lingerie/colours/black+red
     This lets the router recognise every combination of selected colours without breaking the URL structure.
     */
    const COLOUR_PREFIX = '/colours/';

    const normalisedValue = value?.toLowerCase();
    const shouldUsePath = pathFacetColours.includes(normalisedValue);

    if (shouldUsePath) {
      const pathWithoutLocale = pathname.replace(basePath, '/');
      let newPath;

      let isRemovingFacet = false;

      if (pathWithoutLocale?.includes(COLOUR_PREFIX)) {
        const [segment] = pathWithoutLocale.split(COLOUR_PREFIX)[1].split('/');
        const colours = [
          ...new Set([...segment.split('+'), value].filter(Boolean)),
        ];

        if (segment.includes(value)) {
          isRemovingFacet = true;
          const updatedColours = colours.filter(
            colour => colour && colour !== value
          );

          if (updatedColours.length === 0) {
            newPath = `${pathWithoutLocale.split(COLOUR_PREFIX)[0]}/`;
          } else {
            newPath = `${
              pathWithoutLocale.split(COLOUR_PREFIX)[0]
            }${COLOUR_PREFIX}${updatedColours.join('+')}/`;
          }
        } else {
          newPath = `${
            pathWithoutLocale.split(COLOUR_PREFIX)[0]
          }${COLOUR_PREFIX}${colours.join('+')}/`;
        }
      } else {
        newPath = `${pathWithoutLocale.replace(
          /\/$/,
          ''
        )}${COLOUR_PREFIX}${value}/`;
      }

      const newSearch = resetPageParam(search);

      const span = (
        <span className="c-results-facet__label">
          {swatch && (
            <span className="c-swatch" style={{ background: swatch }} />
          )}
          {`${name}${!isNaN(count) ? ` (${count})` : ''}`}
        </span>
      );

      return (
        <Link
          to={`${newPath}${newSearch}`}
          onClick={e => e.preventDefault()}
          className={linkClass}
          {...(newPath.includes('+') && { rel: 'nofollow' })}
        >
          <button
            type="button"
            className="c-results-facet__button"
            onClick={e => {
              e.stopPropagation();
              onChange(!selected, value, 'colour');
              if (isRemovingFacet) {
                props.updateHrefLang(true);
              }
            }}
          >
            {elementType === 'div' && input}
            {elementType === 'div' ? label : span}
          </button>
        </Link>
      );
    }
  }

  return (
    <li {...containerProps}>
      {input}
      {label}
    </li>
  );
};

FacetInput.propTypes = {
  className: PropTypes.string,
  elementType: PropTypes.oneOf(['li', 'div']).isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  count: PropTypes.number,
  onChange: PropTypes.func,
  checked: PropTypes.bool,
  selected: PropTypes.bool,
  swatch: PropTypes.string,
  value: PropTypes.string.isRequired,
  facetType: PropTypes.string,
  pathname: PropTypes.string,
  search: PropTypes.string,
  basePath: PropTypes.string,
  updateHrefLang: PropTypes.func,
};

FacetInput.defaultProps = {
  elementType: 'li',
  onChange: () => {},
};

const mapStateToProps = state => ({
  basePath: state.app.config.basePath,
});

const mapDispatchToProps = dispatch => ({
  updateHrefLang: href => dispatch(updateHrefLang(href)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FacetInput);
